/** Tutorial / clay-pigeon practice intro — shown every time practice starts. */

export type TutorialRow = {
  /** Key / control label(s), e.g. "← →" or "Slider" */
  keys: string[];
  /** What that control does */
  action: string;
};

export type TutorialVariant = {
  /** One short sentence under the title */
  lead: string;
  /** Optional scannable control list */
  rows?: TutorialRow[];
  /** Optional short tip under the list */
  tip?: string;
};

export type TutorialStep = {
  title: string;
  desktop: TutorialVariant;
  mobile: TutorialVariant;
};

/**
 * Controls (must stay in sync with client/main.ts):
 *
 * Desktop (no on-screen angle pad):
 *   ← / →     select angle digit
 *   ↑ / ↓     change selected digit
 *   Shift+↑/↓ jump angle by 5°
 *   + / −     shot power (±1)
 *   slider    shot power (also)
 *   Space     fire
 *
 * Mobile / tablet (on-screen pad, touch-row visible ≤1024px):
 *   ◀ / ▶     select angle digit
 *   + / −     change selected digit (hold to repeat)
 *   slider    shot power
 *   Fire      launch
 */
export const TUTORIAL_STEPS: TutorialStep[] = [
  {
    title: 'Aim',
    desktop: {
      lead: 'Aim by editing one digit of the angle at a time.',
      rows: [
        { keys: ['←', '→'], action: 'Choose which digit to edit' },
        { keys: ['↑', '↓'], action: 'Change the selected digit' },
        { keys: ['Shift', '↑', '↓'], action: 'Jump by 5°' },
      ],
      tip: 'Finer digits help thread shots between planets.',
    },
    mobile: {
      lead: 'Aim with the on-screen controls under the angle.',
      rows: [
        { keys: ['◀', '▶'], action: 'Choose which digit to edit' },
        { keys: ['+', '−'], action: 'Change it (hold to repeat)' },
      ],
      tip: 'Finer digits help thread shots between planets.',
    },
  },
  {
    title: 'Power',
    desktop: {
      lead: 'Choose your shot strength.',
      rows: [{ keys: ['+', '−', 'Slider'], action: 'Adjust power' }],
      tip: 'Each shot costs available power — the red number above the angle display.',
    },
    mobile: {
      lead: 'Choose your shot strength.',
      rows: [{ keys: ['Slider'], action: 'Adjust power' }],
      tip: 'Each shot costs available power — the red number above the angle display.',
    },
  },
  {
    title: 'Fire',
    desktop: {
      lead: 'Launch when you are ready. Planets bend your shot with gravity.',
      rows: [
        { keys: ['Space'], action: 'Fire' },
        { keys: ['Fire'], action: 'Or click the button' },
      ],
    },
    mobile: {
      lead: 'Launch when you are ready. Planets bend your shot with gravity.',
      rows: [{ keys: ['Fire'], action: 'Tap to launch' }],
    },
  },
  {
    title: 'Score',
    desktop: {
      lead: 'Hit the green targets. Survive and go for long shots.',
      rows: [
        { keys: ['Hit'], action: 'Score points (longer flights score more)' },
        { keys: ['Death'], action: 'Lose 1 point' },
      ],
    },
    mobile: {
      lead: 'Hit the green targets. Survive and go for long shots.',
      rows: [
        { keys: ['Hit'], action: 'Score points (longer flights score more)' },
        { keys: ['Death'], action: 'Lose 1 point' },
      ],
    },
  },
];

export function isTouchUi(): boolean {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia('(hover: none) and (pointer: coarse)').matches ||
    (window.matchMedia('(max-width: 1024px)').matches && 'ontouchstart' in window)
  );
}
